import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api/axios";
import {
  Search,
  RefreshCw,
  Eye,
  X,
  Loader2,
  CalendarDays,
  Users,
  Ship,
  CheckCircle2,
  AlertCircle,
  Ban,
  Inbox,
  BadgeCheck,
  Flag,
  Check,
} from "lucide-react";
import "./CruiseBookingsManagement.css";

/* ============================================================
   API  (backend: /api/vendor/cruise-bookings)
============================================================ */

const BASE = "/api/vendor/cruise-bookings";

const authConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("vendorToken")}` },
});

const listFrom = (data) => {
  if (Array.isArray(data)) return data;
  return data?.bookings || data?.data?.bookings || data?.data || [];
};

const errorText = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  "Something went wrong.";

/* ============================================================
   TABS (4 sections)
============================================================ */

const TABS = [
  {
    key: "incoming",
    label: "Incoming",
    hint: "New requests waiting for you",
    statuses: ["pending", "reserved"],
    icon: Inbox,
    empty: "No incoming bookings right now.",
  },
  {
    key: "confirmed",
    label: "Confirmed",
    hint: "Accepted, sailing yet to happen",
    statuses: ["confirmed"],
    icon: BadgeCheck,
    empty: "No confirmed bookings yet.",
  },
  {
    key: "completed",
    label: "Completed",
    hint: "Sailing finished",
    statuses: ["completed"],
    icon: Flag,
    empty: "No completed bookings yet.",
  },
  {
    key: "cancelled",
    label: "Cancelled",
    hint: "Cancelled by you or the guest",
    statuses: ["cancelled"],
    icon: Ban,
    empty: "No cancelled bookings.",
  },
];

/* ============================================================
   HELPERS
============================================================ */

const dateText = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const dateTimeText = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const money = (value, currency = "INR") => {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "INR",
      maximumFractionDigits: 2,
    }).format(Number(value || 0));
  } catch {
    return `${currency || "INR"} ${Number(value || 0).toFixed(2)}`;
  }
};

const passengerName = (p) => [p?.firstName, p?.lastName].filter(Boolean).join(" ") || "Passenger";
const shipName = (s) => s?.shipName || s?.name || "Cruise Ship";
const cabinName = (c) => c?.cabinName || c?.name || c?.cabinType || c?.categoryName || "Cabin";
const sailingCode = (s) => s?.sailingCode || s?.code || "Sailing";
const guests = (b) => b?.quantity || b?.passengers?.length || 0;

const tabOf = (booking) =>
  TABS.find((t) => t.statuses.includes(booking?.bookingStatus))?.key || "incoming";

/* ============================================================
   COMPONENT
============================================================ */

export default function CruiseBookingsManagement() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [tab, setTab] = useState("incoming");
  const [search, setSearch] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");

  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(""); // "confirm" | "complete" | "cancel" | booking id
  const [cancelReason, setCancelReason] = useState("");

  /* ---------- load ---------- */
  const loadBookings = async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError("");
      const res = await api.get(BASE, authConfig());
      setBookings(listFrom(res.data));
    } catch (err) {
      setError(errorText(err));
      setBookings([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, []);

  /* ---------- counts + filtered ---------- */
  const counts = useMemo(() => {
    const c = { incoming: 0, confirmed: 0, completed: 0, cancelled: 0 };
    bookings.forEach((b) => {
      c[tabOf(b)] += 1;
    });
    return c;
  }, [bookings]);

  const activeTab = TABS.find((t) => t.key === tab);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return bookings.filter((b) => {
      if (!activeTab.statuses.includes(b.bookingStatus)) return false;
      if (paymentStatus && b.paymentStatus !== paymentStatus) return false;
      if (!q) return true;
      const text = [
        b.bookingReference,
        b.customerName,
        b.customerEmail,
        b.customerPhone,
        sailingCode(b.sailingId),
        shipName(b.shipId),
        cabinName(b.cabinId),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return text.includes(q);
    });
  }, [bookings, activeTab, search, paymentStatus]);

  /* ---------- details ---------- */
  const openDetails = async (booking) => {
    setSelected(booking);
    setCancelReason("");
    try {
      setDetailLoading(true);
      const res = await api.get(`${BASE}/${booking._id}`, authConfig());
      setSelected(res.data?.booking || res.data?.data || booking);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetails = () => {
    if (busy) return;
    setSelected(null);
    setCancelReason("");
  };

  /* ---------- actions ---------- */
  const runAction = async (booking, action) => {
    const messages = {
      confirm:
        booking.paymentStatus === "paid"
          ? "Confirm this booking?"
          : "Payment is not received yet. Confirm this booking anyway?",
      complete: "Mark this booking as completed?",
      cancel:
        booking.paymentStatus === "paid"
          ? "Payment is already received. Cancel this booking? You will need to process the refund."
          : "Cancel this booking?",
    };
    if (!window.confirm(messages[action])) return;

    try {
      setBusy(`${action}:${booking._id}`);
      setError("");
      setNotice("");
      const res = await api.patch(
        `${BASE}/${booking._id}/${action}`,
        action === "cancel" ? { reason: cancelReason } : {},
        authConfig()
      );
      const updated = res.data?.booking || res.data?.data;
      if (updated) {
        setBookings((prev) => prev.map((x) => (x._id === updated._id ? updated : x)));
        setSelected((cur) => (cur && cur._id === updated._id ? updated : cur));
        setTab(tabOf(updated));
      } else {
        await loadBookings(true);
      }
      setNotice(res.data?.message || "Booking updated.");
      if (action !== "confirm") setSelected(null);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy("");
    }
  };

  const isBusy = (action, id) => busy === `${action}:${id}`;

  /* ---------- action buttons for a booking ---------- */
  const ActionButtons = ({ booking, compact = false }) => {
    const s = booking.bookingStatus;
    const cls = compact ? "cbm-btn cbm-btn-sm" : "cbm-btn";
    return (
      <>
        {["pending", "reserved"].includes(s) && (
          <button
            className={`${cls} cbm-btn-green`}
            disabled={!!busy}
            onClick={(e) => {
              e.stopPropagation();
              runAction(booking, "confirm");
            }}
          >
            {isBusy("confirm", booking._id) ? <Loader2 size={15} className="cbm-spin" /> : <Check size={15} />}
            Confirm
          </button>
        )}
        {s === "confirmed" && (
          <button
            className={`${cls} cbm-btn-blue`}
            disabled={!!busy}
            onClick={(e) => {
              e.stopPropagation();
              runAction(booking, "complete");
            }}
          >
            {isBusy("complete", booking._id) ? <Loader2 size={15} className="cbm-spin" /> : <Flag size={15} />}
            Mark completed
          </button>
        )}
      </>
    );
  };

  const canCancel = (b) => ["pending", "reserved", "confirmed"].includes(b?.bookingStatus);

  /* ============================================================ RENDER */
  return (
    <div className="cbm-page">
      {/* heading */}
      <div className="cbm-heading">
        <div>
          <span className="cbm-eyebrow">CRUISE MANAGEMENT</span>
          <h1>Bookings Management</h1>
          <p>Accept new requests, track confirmed sailings and review past bookings.</p>
        </div>
        <button className="cbm-refresh" onClick={() => loadBookings(true)} disabled={loading || refreshing}>
          <RefreshCw size={17} className={refreshing ? "cbm-spin" : ""} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="cbm-alert cbm-alert-error">
          <AlertCircle size={18} />
          <span>{error}</span>
          <button onClick={() => setError("")} aria-label="Dismiss"><X size={16} /></button>
        </div>
      )}
      {notice && (
        <div className="cbm-alert cbm-alert-ok">
          <CheckCircle2 size={18} />
          <span>{notice}</span>
          <button onClick={() => setNotice("")} aria-label="Dismiss"><X size={16} /></button>
        </div>
      )}

      {/* 4 sections */}
      <div className="cbm-tabs" role="tablist">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={tab === t.key}
              className={`cbm-tab cbm-tab-${t.key}${tab === t.key ? " active" : ""}`}
              onClick={() => setTab(t.key)}
            >
              <span className="cbm-tab-icon"><Icon size={20} /></span>
              <span className="cbm-tab-text">
                <strong>{t.label}</strong>
                <small>{t.hint}</small>
              </span>
              <span className="cbm-tab-count">{counts[t.key]}</span>
            </button>
          );
        })}
      </div>

      {/* filters */}
      <div className="cbm-filters">
        <div className="cbm-search">
          <Search size={18} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search booking, guest, sailing or ship..."
          />
        </div>
        <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}>
          <option value="">All payments</option>
          <option value="pending">Payment pending</option>
          <option value="processing">Processing</option>
          <option value="paid">Paid</option>
          <option value="failed">Failed</option>
          <option value="refunded">Refunded</option>
          <option value="partially-refunded">Partially refunded</option>
        </select>
      </div>

      {/* table */}
      <div className="cbm-card">
        <div className="cbm-card-head">
          <h2>{activeTab.label} bookings</h2>
          <span>{filtered.length} booking{filtered.length === 1 ? "" : "s"}</span>
        </div>

        {loading ? (
          <div className="cbm-empty">
            <Loader2 size={28} className="cbm-spin" />
            <span>Loading bookings...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="cbm-empty">
            <CalendarDays size={38} />
            <h3>{search || paymentStatus ? "No matching bookings" : activeTab.empty}</h3>
            {(search || paymentStatus) && <p>Try clearing the search or payment filter.</p>}
          </div>
        ) : (
          <div className="cbm-table-wrap">
            <table className="cbm-table">
              <thead>
                <tr>
                  <th>Booking</th>
                  <th>Guest</th>
                  <th>Sailing</th>
                  <th>Ship / Cabin</th>
                  <th>Guests</th>
                  <th>Total</th>
                  <th>Payment</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => (
                  <tr key={b._id} onClick={() => openDetails(b)}>
                    <td>
                      <strong>{b.bookingReference || "—"}</strong>
                      <small>Booked {dateText(b.createdAt)}</small>
                    </td>
                    <td>
                      <strong>{b.customerName || "—"}</strong>
                      <small>{b.customerPhone || b.customerEmail || "—"}</small>
                    </td>
                    <td>
                      <strong>{sailingCode(b.sailingId)}</strong>
                      <small>{dateText(b.sailingId?.departureDate)}</small>
                    </td>
                    <td>
                      <span className="cbm-inline"><Ship size={14} />{shipName(b.shipId)}</span>
                      <small>{cabinName(b.cabinId)}</small>
                    </td>
                    <td><span className="cbm-inline"><Users size={14} />{guests(b)}</span></td>
                    <td><strong>{money(b.totalAmount, b.currency)}</strong></td>
                    <td><span className={`cbm-pill cbm-pay-${b.paymentStatus}`}>{b.paymentStatus}</span></td>
                    <td>
                      <div className="cbm-row-actions">
                        <ActionButtons booking={b} compact />
                        <button
                          className="cbm-btn cbm-btn-sm cbm-btn-ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDetails(b);
                          }}
                        >
                          <Eye size={15} /> View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* details modal */}
      {selected && (
        <div className="cbm-overlay" onMouseDown={closeDetails}>
          <div className="cbm-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="cbm-modal-head">
              <div>
                <span>BOOKING DETAILS</span>
                <h2>{selected.bookingReference || "Cruise Booking"}</h2>
                <div className="cbm-modal-badges">
                  <span className={`cbm-pill cbm-st-${selected.bookingStatus}`}>{selected.bookingStatus}</span>
                  <span className={`cbm-pill cbm-pay-${selected.paymentStatus}`}>payment {selected.paymentStatus}</span>
                </div>
              </div>
              <button onClick={closeDetails} aria-label="Close"><X size={20} /></button>
            </div>

            {detailLoading ? (
              <div className="cbm-modal-loading">
                <Loader2 size={30} className="cbm-spin" />
                Loading booking details...
              </div>
            ) : (
              <>
                <div className="cbm-modal-body">
                  <section>
                    <h3>Guest details</h3>
                    <div className="cbm-grid">
                      <div><span>Name</span><strong>{selected.customerName || "—"}</strong></div>
                      <div><span>Email</span><strong>{selected.customerEmail || "—"}</strong></div>
                      <div><span>Phone</span><strong>{selected.customerPhone || "—"}</strong></div>
                      <div><span>Booked on</span><strong>{dateTimeText(selected.createdAt)}</strong></div>
                    </div>
                  </section>

                  <section>
                    <h3>Cruise details</h3>
                    <div className="cbm-grid">
                      <div><span>Sailing</span><strong>{sailingCode(selected.sailingId)}</strong></div>
                      <div><span>Ship</span><strong>{shipName(selected.shipId)}</strong></div>
                      <div><span>Cabin</span><strong>{cabinName(selected.cabinId)}</strong></div>
                      <div><span>Guests</span><strong>{guests(selected)}</strong></div>
                      <div><span>Departure</span><strong>{dateText(selected.sailingId?.departureDate)}</strong></div>
                      <div><span>Return</span><strong>{dateText(selected.sailingId?.returnDate)}</strong></div>
                    </div>
                  </section>

                  <section>
                    <h3>Passengers</h3>
                    <div className="cbm-passengers">
                      {(selected.passengers || []).map((p, i) => (
                        <div key={p._id || i}>
                          <strong>{passengerName(p)}</strong>
                          <span>
                            {p.gender || "other"}
                            {p.age != null ? ` • ${p.age} yrs` : ""}
                            {p.nationality ? ` • ${p.nationality}` : ""}
                          </span>
                          <small>
                            {p.passportNumber ? `Passport ${p.passportNumber}` : p.email || p.phone || "—"}
                          </small>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section>
                    <h3>Payment & amount</h3>
                    <div className="cbm-amounts">
                      <div><span>Base amount</span><strong>{money(selected.baseAmount, selected.currency)}</strong></div>
                      <div><span>Tax</span><strong>{money(selected.taxAmount, selected.currency)}</strong></div>
                      <div><span>Port charges</span><strong>{money(selected.portCharges, selected.currency)}</strong></div>
                      <div><span>Service charges</span><strong>{money(selected.serviceCharges, selected.currency)}</strong></div>
                      <div><span>Discount</span><strong>- {money(selected.discountAmount, selected.currency)}</strong></div>
                      <div className="total"><span>Total</span><strong>{money(selected.totalAmount, selected.currency)}</strong></div>
                    </div>
                    <p className="cbm-meta">
                      Method: <strong>{selected.paymentMethod || "—"}</strong>
                      {selected.paymentId ? <> • Payment ID: <strong>{selected.paymentId}</strong></> : null}
                    </p>
                  </section>

                  {selected.specialRequests && (
                    <section>
                      <h3>Special requests</h3>
                      <p className="cbm-note">{selected.specialRequests}</p>
                    </section>
                  )}

                  {selected.bookingStatus === "cancelled" && (
                    <section>
                      <h3>Cancellation</h3>
                      <p className="cbm-note">
                        {selected.cancellationReason || "No reason given."}
                        <br />
                        <small>Cancelled on {dateTimeText(selected.cancelledAt)}</small>
                      </p>
                    </section>
                  )}

                  {canCancel(selected) && (
                    <section>
                      <h3>Cancel booking</h3>
                      <textarea
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        placeholder="Cancellation reason (optional, shown in the record)"
                        rows={3}
                      />
                    </section>
                  )}
                </div>

                <div className="cbm-modal-footer">
                  <button className="cbm-btn cbm-btn-ghost" onClick={closeDetails} disabled={!!busy}>Close</button>
                  <div className="cbm-footer-right">
                    {canCancel(selected) && (
                      <button
                        className="cbm-btn cbm-btn-red"
                        disabled={!!busy}
                        onClick={() => runAction(selected, "cancel")}
                      >
                        {isBusy("cancel", selected._id) ? <Loader2 size={15} className="cbm-spin" /> : <Ban size={15} />}
                        Cancel booking
                      </button>
                    )}
                    <ActionButtons booking={selected} />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}